/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Wed May 15 2024
 **/

import { useConfirm, useFileManager } from "@/frameworks/core/hooks";
import { useFileExtensions } from "@/frameworks/core/utils";
import { useUnityCommand } from "@/frameworks/core/unity";
import { usePersistUser } from "@/frameworks/core/persistence";
import { Button } from "@/frameworks/designStuff/components";
import { _P } from "@rfice/language";

import type { UploadToSpaceButtonProps } from "./UploadToSpaceButton";

export default function UploadAudioZoneButton({ file, size, fill }: UploadToSpaceButtonProps) {
	const { convertDocToImage } = useFileManager();
	const { uploadFileToUnity, uploadLinkToUnity } = useUnityCommand();
	const me = usePersistUser("me");
	const { getFileExtension } = useFileExtensions();
	const { confirm } = useConfirm();

	const uploadLink = () => {
		uploadLinkToUnity({
			description: file.description,
			image: file.imageUrl,
			title: file.title,
			url: file.link || ""
		});
	};

	const uploadFileWithParams = (docImageId?: string) => {
		if (!me || !me.currentSpace) return;

		uploadFileToUnity({
			createdBy: file.createBy || "",
			docImageId: docImageId,
			fileMimeType: file.fileMimeType || "",
			fileName: file.name || "",
			fileSize: file.fileSize || 0,
			createDateTime: file.createDateTime || 0,
			fileDisplaySize: file.fileDisplaySize || "0",
			sceneLevel: me.currentSpace.spaceScene.sceneLevel,
			spaceID: me.currentSpace.spaceId,
			spaceType: me.currentSpace.spaceScene.sceneType?.typeNumber || 0,
			thumbnail: file.docThumbnailPath ?? "",
			uploadType: "fileBox",
			fileID: file.id,
			filePath: file.filePath || ""
		});
	};

	const uploadFile = async () => {
		if (getFileExtension(file.fileMimeType || "") === "image") {
			uploadFileWithParams();
			return;
		}

		try {
			const { docImageId } = await convertDocToImage({ fileId: file.id });
			uploadFileWithParams(docImageId);
		} catch {
			uploadFileWithParams();
		}
	};

	const share3D = () => {
		confirm(
			_P("현재 참여 중인 {{space}} 스페이스에 선택한 파일을 업로드하시겠습니까?", {
				space: me.currentSpace?.spaceName
			}),
			{
				onConfirm() {
					if (file.fileBoxType === "LINK") {
						uploadLink();
					} else {
						uploadFile();
					}
				}
			}
		);
	};
	return <Button.Icon name="Upload24" size={size} filled="transparent" fill={fill} onClick={share3D} />;
}
